import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaShieldAlt } from "react-icons/fa";
import api from "../../utils/api";
import AddUserModal from "../../components/AddUserModal";
import EditUserModal from "../../components/EditUserModal";
import UploadResearchModal from "../../components/UploadResearchModal";
import ProcessRequestModal from "../../components/ProcessRequestModal";
import ResearchManagementModal from "../../components/ResearchManagementModal";
import EditResearchModal from "../../components/EditResearchModal";
import AnalyticsCharts from "../../components/AnalyticsCharts";
import { formatRequestTimestamp } from "../../utils/formatDateTime";
import { isHighSimilarity } from "../../utils/plagiarismPolicy";

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [research, setResearch] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUploadResearchModal, setShowUploadResearchModal] = useState(false);
  const [showProcessRequestModal, setShowProcessRequestModal] = useState(false);
  const [showResearchManagementModal, setShowResearchManagementModal] = useState(false);
  const [showEditResearchModal, setShowEditResearchModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedResearch, setSelectedResearch] = useState(null);
  const [researchToEdit, setResearchToEdit] = useState(null);
  const [researchSearchTerm, setResearchSearchTerm] = useState("");
  const [plagiarismPolicy, setPlagiarismPolicy] = useState(null);
  const [bulkCheckLoading, setBulkCheckLoading] = useState(false);
  const [bulkCheckMessage, setBulkCheckMessage] = useState("");
  const navigate = useNavigate();

  const pendingNeedingCheckCount = useMemo(() => {
    return research.filter(
      (doc) =>
        doc.status === "pending" &&
        doc.plagiarismCheck?.status !== "processing" &&
        doc.plagiarismCheck?.status !== "completed"
    ).length;
  }, [research]);

  const filteredResearch = useMemo(() => {
    const q = researchSearchTerm.trim().toLowerCase();
    if (!q) return research;
    return research.filter((doc) => {
      const title = doc.title?.toLowerCase() || "";
      const abstract = doc.abstract?.toLowerCase() || "";
      const campus = doc.campus?.toLowerCase() || "";
      const department = doc.department?.toLowerCase() || "";
      const status = doc.status?.toLowerCase() || "";
      const pdf = doc.pdfFilename?.toLowerCase() || "";
      const authors = Array.isArray(doc.authors)
        ? doc.authors.join(" ").toLowerCase()
        : String(doc.authors || "").toLowerCase();
      const keywords = Array.isArray(doc.keywords)
        ? doc.keywords.join(" ").toLowerCase()
        : String(doc.keywords || "").toLowerCase();
      const uploader = doc.uploadedBy?.name?.toLowerCase() || doc.uploadedBy?.email?.toLowerCase() || "";
      return (
        title.includes(q) ||
        abstract.includes(q) ||
        authors.includes(q) ||
        keywords.includes(q) ||
        campus.includes(q) ||
        department.includes(q) ||
        status.includes(q) ||
        pdf.includes(q) ||
        uploader.includes(q) ||
        String(doc.year || "").includes(q)
      );
    });
  }, [research, researchSearchTerm]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      setUser(JSON.parse(userData));
    }
    loadDashboardData();
  }, []);

  useEffect(() => {
    api
      .get("/research/plagiarism-policy")
      .then(({ data }) => setPlagiarismPolicy(data))
      .catch(() => setPlagiarismPolicy(null));
  }, []);

  const handleBulkPlagiarismCheck = async () => {
    setBulkCheckLoading(true);
    setBulkCheckMessage("");
    try {
      const { data } = await api.post("/research/bulk-plagiarism-check");
      setBulkCheckMessage(data.message || `Queued ${data.queued} document(s).`);
      loadDashboardData();
    } catch (err) {
      setBulkCheckMessage(err.response?.data?.error || "Bulk check failed.");
    } finally {
      setBulkCheckLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, usersRes, researchRes, requestsRes] = await Promise.all([
        api.get("/analytics/summary"),
        api.get("/users"),
        api.get("/research"),
        api.get("/request")
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setResearch(researchRes.data.research);
      setRequests(requestsRes.data.requests);
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const handleUserAdded = () => {
    loadDashboardData(); // Refresh the users list
  };

  const handleUserUpdated = () => {
    loadDashboardData(); // Refresh the users list
  };

  const handleResearchAdded = () => {
    loadDashboardData(); // Refresh the research list
  };

  const handleRequestProcessed = () => {
    loadDashboardData(); // Refresh the requests list
  };

  const handleProcessRequest = (request) => {
    setSelectedRequest(request);
    setShowProcessRequestModal(true);
  };

  const handleResearchManagement = (research) => {
    setSelectedResearch(research);
    setShowResearchManagementModal(true);
  };

  const handleEditResearch = (doc) => {
    setResearchToEdit(doc);
    setShowEditResearchModal(true);
  };

  const handleResearchUpdated = () => {
    loadDashboardData(); // Refresh the research list
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditUserModal(true);
  };

  const handleDeleteUser = async (userToDelete) => {
    // Prevent deleting admin users
    if (userToDelete.role === 'admin') {
      alert('Cannot delete admin users');
      return;
    }

    // Prevent deleting the current user
    if (userToDelete._id === user?._id) {
      alert('Cannot delete your own account');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${userToDelete.name}? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/users/${userToDelete._id}`);
      loadDashboardData(); // Refresh the users list
    } catch (error) {
      console.error("Error deleting user:", error);
      alert(error.response?.data?.error || "Failed to delete user");
    }
  };

  const renderOverview = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <div className="bg-green-800 p-4 rounded-lg">
        <h3 className="text-lg font-semibold">Total Research</h3>
        <p className="text-2xl font-bold">{stats.summary?.totalResearch || 0}</p>
      </div>
      <div className="bg-blue-800 p-4 rounded-lg">
        <h3 className="text-lg font-semibold">Total Users</h3>
        <p className="text-2xl font-bold">{stats.summary?.totalUsers || 0}</p>
      </div>
      <div className="bg-yellow-800 p-4 rounded-lg">
        <h3 className="text-lg font-semibold">Total Requests</h3>
        <p className="text-2xl font-bold">{stats.summary?.totalRequests || 0}</p>
      </div>
      <div className="bg-red-800 p-4 rounded-lg">
        <h3 className="text-lg font-semibold">Pending Requests</h3>
        <p className="text-2xl font-bold">{stats.summary?.pendingRequests || 0}</p>
      </div>
    </div>
  );

  const renderUserManagement = () => (
    <div className="bg-gray-800 p-6 rounded-lg">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">User Management</h3>
        <button 
          onClick={() => setShowAddUserModal(true)}
          className="bg-green-600 px-4 py-2 rounded hover:bg-green-700"
        >
          Add User
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left p-2">Name</th>
              <th className="text-left p-2">Login email</th>
              <th className="text-left p-2">Role</th>
              <th className="text-left p-2">Campus</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user._id} className="border-b border-gray-700">
                <td className="p-2">{user.name}</td>
                <td className="p-2">{user.email}</td>
                <td className="p-2">
                  <span className={`px-2 py-1 rounded text-xs ${
                    user.role === 'admin' ? 'bg-red-600' :
                    user.role === 'coordinator' ? 'bg-blue-600' : 'bg-green-600'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td className="p-2">{user.campus}</td>
                <td className="p-2">
                  <span className={`px-2 py-1 rounded text-xs ${
                    user.isActive ? 'bg-green-600' : 'bg-red-600'
                  }`}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="p-2">
                  <button 
                    onClick={() => handleEditUser(user)}
                    className="bg-blue-600 px-2 py-1 rounded text-xs mr-1 hover:bg-blue-700"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDeleteUser(user)}
                    className="bg-red-600 px-2 py-1 rounded text-xs hover:bg-red-700"
                    disabled={user.role === 'admin'}
                    title={user.role === 'admin' ? 'Cannot delete admin users' : ''}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderResearchManagement = () => {
    const similarityThreshold = plagiarismPolicy?.maxSimilarityForApproval ?? 25;

    return (
    <div className="bg-gray-800 p-6 rounded-lg">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
        <h3 className="text-xl font-semibold">Research Documents</h3>
        <div className="flex flex-wrap gap-2 shrink-0 self-start sm:self-auto">
          <button
            type="button"
            onClick={handleBulkPlagiarismCheck}
            disabled={bulkCheckLoading || pendingNeedingCheckCount === 0}
            title={
              pendingNeedingCheckCount === 0
                ? "No pending documents need a plagiarism check"
                : `Queue DrillBit for ${pendingNeedingCheckCount} pending document(s)`
            }
            className="bg-indigo-600 px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <FaShieldAlt />
            {bulkCheckLoading ? "Queuing…" : `Check all pending (${pendingNeedingCheckCount})`}
          </button>
          <button
            onClick={() => setShowUploadResearchModal(true)}
            className="bg-green-600 px-4 py-2 rounded hover:bg-green-700"
          >
            Upload Research
          </button>
        </div>
      </div>

      {bulkCheckMessage && (
        <p className="text-sm text-indigo-300 mb-4">{bulkCheckMessage}</p>
      )}

      {plagiarismPolicy?.enforcementEnabled && (
        <p className="text-xs text-gray-400 mb-4">
          Approval blocked when similarity is {similarityThreshold}% or higher
          {plagiarismPolicy.requireCompletedCheck ? ", or when DrillBit check is incomplete" : ""}.
        </p>
      )}

      <div className="relative mb-4">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
        <input
          type="search"
          placeholder="Search by title, authors, abstract, campus, department, status, year, file name…"
          value={researchSearchTerm}
          onChange={(e) => setResearchSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-gray-700 text-white border border-gray-600 placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          aria-label="Search research documents"
        />
      </div>
      <p className="text-sm text-gray-400 mb-4">
        Showing {filteredResearch.length} of {research.length} document{research.length !== 1 ? "s" : ""}
        {researchSearchTerm.trim() ? ` matching “${researchSearchTerm.trim()}”` : ""}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredResearch.map((doc) => (
          <div key={doc._id} className="bg-gray-700 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">{doc.title}</h4>
            <p className="text-sm text-gray-300 mb-2">
              Authors: {doc.authors.join(", ")}
            </p>
            <p className="text-sm text-gray-300 mb-2">
              Year: {doc.year}
            </p>
            <p className="text-sm text-gray-300 mb-2">
              Campus: {doc.campus}
            </p>
            <p className="text-sm text-gray-300 mb-2">
              Status: <span className={`px-2 py-1 rounded text-xs ${
                doc.status === 'approved' ? 'bg-green-600' :
                doc.status === 'rejected' ? 'bg-red-600' : 'bg-yellow-600'
              }`}>
                {doc.status}
              </span>
            </p>
            <p className="text-sm text-gray-300 mb-2">
              <span className="text-gray-400">Visibility: </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                doc.isPublic ? "bg-emerald-900 text-emerald-200" : "bg-slate-700 text-slate-200"
              }`}>
                {doc.isPublic ? "Public" : "Private"}
              </span>
            </p>
            <p className="text-sm text-gray-300 mb-2">
              <span className="text-gray-400">Plagiarism: </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                doc.plagiarismCheck?.status === "completed"
                  ? (isHighSimilarity(doc.plagiarismCheck.similarityPercent, similarityThreshold)
                    ? "bg-red-900 text-red-200"
                    : "bg-green-900 text-green-200")
                  : doc.plagiarismCheck?.status === "processing"
                  ? "bg-blue-900 text-blue-200"
                  : doc.plagiarismCheck?.status === "failed"
                  ? "bg-red-900 text-red-200"
                  : "bg-gray-700 text-gray-300"
              }`}>
                {doc.plagiarismCheck?.status === "completed" && doc.plagiarismCheck.similarityPercent != null
                  ? `${doc.plagiarismCheck.similarityPercent}% similar`
                  : doc.plagiarismCheck?.status || "pending"}
              </span>
            </p>
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={() => handleResearchManagement(doc)}
                className="bg-blue-600 px-3 py-1 rounded text-sm hover:bg-blue-700"
              >
                Manage
              </button>
              <button 
                type="button"
                onClick={() => handleEditResearch(doc)}
                className="bg-green-600 px-3 py-1 rounded text-sm hover:bg-green-700"
              >
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredResearch.length === 0 && (
        <div className="text-center py-12 text-gray-400 border border-dashed border-gray-600 rounded-lg">
          {research.length === 0
            ? "No research documents yet. Upload one to get started."
            : "No documents match your search. Try different keywords."}
        </div>
      )}
    </div>
    );
  };

  const renderAccessRequests = () => (
    <div className="bg-gray-800 p-6 rounded-lg">
      <h3 className="text-xl font-semibold mb-4">Access Requests</h3>
      <div className="space-y-4">
        {requests.map((request) => (
          <div key={request._id} className="bg-gray-700 p-4 rounded-lg">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-semibold">{request.studentId?.name}</p>
                <p className="text-sm text-gray-300">
                  Requesting: {request.researchId?.title}
                </p>
                <p className="text-sm text-gray-300">
                  Reason: {request.reason}
                </p>
                <p className="text-sm text-gray-300">
                  <span className="text-gray-500">Requested:</span>{" "}
                  {formatRequestTimestamp(request.dateRequested)}
                </p>
                {request.dateProcessed && (
                  <p className="text-sm text-gray-300">
                    <span className="text-gray-500">Processed:</span>{" "}
                    {formatRequestTimestamp(request.dateProcessed)}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => handleProcessRequest(request)}
                  className="bg-green-600 px-3 py-1 rounded text-sm"
                >
                  Process Request
                </button>
                <button className="bg-red-600 px-3 py-1 rounded text-sm">Decline</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="bg-gray-800 p-6 rounded-lg">
      <h3 className="text-xl font-semibold mb-4">System Analytics</h3>
      <AnalyticsCharts 
        stats={stats} 
        research={research} 
        requests={requests} 
      />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 p-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">
            <span className="font-extrabold">SKSU Digital Research Hub</span> &amp; Open Access Journal - Admin Dashboard
          </h1>
          <div className="flex items-center gap-4">
            <span>Welcome, {user?.name}</span>
            <button
              onClick={handleLogout}
              className="bg-red-600 px-4 py-2 rounded hover:bg-red-700"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-gray-700 p-4">
        <div className="flex gap-4">
          {[
            { id: "overview", label: "Overview" },
            { id: "users", label: "User Management" },
            { id: "research", label: "Research Management" },
            { id: "requests", label: "Access Requests" },
            { id: "analytics", label: "Analytics" }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded ${
                activeTab === tab.id
                  ? "bg-gray-600 text-white"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-600"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="p-6">
        {activeTab === "overview" && renderOverview()}
        {activeTab === "users" && renderUserManagement()}
        {activeTab === "research" && renderResearchManagement()}
        {activeTab === "requests" && renderAccessRequests()}
        {activeTab === "analytics" && renderAnalytics()}
      </main>

      {/* Modals */}
      <AddUserModal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        onUserAdded={handleUserAdded}
      />

      <EditUserModal
        isOpen={showEditUserModal}
        onClose={() => setShowEditUserModal(false)}
        user={selectedUser}
        onUserUpdated={handleUserUpdated}
      />

      <UploadResearchModal
        isOpen={showUploadResearchModal}
        onClose={() => setShowUploadResearchModal(false)}
        onResearchAdded={handleResearchAdded}
      />

      <ProcessRequestModal
        isOpen={showProcessRequestModal}
        onClose={() => setShowProcessRequestModal(false)}
        onRequestProcessed={handleRequestProcessed}
        request={selectedRequest}
      />

      <ResearchManagementModal
        isOpen={showResearchManagementModal}
        onClose={() => setShowResearchManagementModal(false)}
        research={selectedResearch}
        onResearchUpdated={handleResearchUpdated}
      />

      <EditResearchModal
        isOpen={showEditResearchModal}
        onClose={() => {
          setShowEditResearchModal(false);
          setResearchToEdit(null);
        }}
        research={researchToEdit}
        onResearchUpdated={handleResearchUpdated}
      />
    </div>
  );
} 